import posthog from 'posthog-js';
import { useAuthStore } from '@/store/authStore';
import { MonitorAlertEventDto, MonitorAlertFullDto } from '@/app/Alert/types';
import { trackShiftStart, trackShiftEnd } from '@/app/Shift/util/trackShiftStartEnd';

// Use localStorage to persist timestamps
const ALERT_SELECTION_TIMES_KEY = 'alert_selection_times';
const SHIFT_BREAK_START_KEY = 'shift_break_start_time';
const SHIFT_START_KEY = 'shift_start_time';

const getStoredTimes = (): { [key: string]: string } => {
  const stored = localStorage.getItem(ALERT_SELECTION_TIMES_KEY);
  return stored ? JSON.parse(stored) : {};
};

const storeTime = (alertId: string, timestamp: string) => {
  const times = getStoredTimes();
  times[alertId] = timestamp;
  localStorage.setItem(ALERT_SELECTION_TIMES_KEY, JSON.stringify(times));
};

const removeTime = (alertId: string) => {
  const times = getStoredTimes();
  delete times[alertId];
  localStorage.setItem(ALERT_SELECTION_TIMES_KEY, JSON.stringify(times));
};

export const useAlertMetrics = () => {
  const auth = useAuthStore();

  const trackAlertSelection = (alertId: string, alertData?: MonitorAlertFullDto) => {
    try {
      const timestamp = new Date().toISOString();
      // Store the selection timestamp
      storeTime(alertId, timestamp);

      posthog.capture('alert_selected', {
        alert_id: alertId,
        user_id: auth.user?.id,
        timestamp: timestamp,
        distinct_id: auth.user?.id, // Required for user identification
        alert: alertData?.alertId || alertId, // Use alertData.alertId if available, fallback to alertId
      });
    } catch (error) {
      console.error('Error tracking alert selection:', error);
    }
  };

  const trackAlertResolved = (alertId: string, resolutionType: string) => {
    try {
      const closedTimestamp = new Date().toISOString();
      const storedTimes = getStoredTimes();
      const selectedTimestamp = storedTimes[alertId];

      if (selectedTimestamp) {
        // Calculate duration in milliseconds and seconds
        const selectedTime = new Date(selectedTimestamp).getTime();
        const closedTime = new Date(closedTimestamp).getTime();
        const durationMs = closedTime - selectedTime;
        const durationSeconds = Math.round(durationMs / 1000);

        // // Format timestamps for readable logging
        // const selectedDate = new Date(selectedTimestamp);
        // const closedDate = new Date(closedTimestamp);

        // console.log('Duration calculation:', {
        //   user_id: auth.user?.id,
        //   username: auth.user?.username,
        //   alertId,
        //   selectedTime: {
        //     raw: selectedTimestamp,
        //     formatted: selectedDate.toLocaleString(),
        //     ms: selectedTime,
        //   },
        //   closedTime: {
        //     raw: closedTimestamp,
        //     formatted: closedDate.toLocaleString(),
        //     ms: closedTime,
        //   },
        //   duration: {
        //     ms: durationMs,
        //     seconds: durationSeconds,
        //     readable: `${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s`,
        //   },
        // });

        const eventData = {
          alert_id: alertId,
          username: auth.user?.username,
          distinct_id: auth.user?.id,
          resolution_type: resolutionType,
          timestamp: closedTimestamp,
          duration_seconds: durationSeconds,
          duration: `${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s`,
          selected_at: selectedTimestamp,
          closed_at: closedTimestamp,
        };
        posthog.capture('alert_closed_duration', eventData);

        // Clean up stored timestamp
        removeTime(alertId);
      } else {
        console.warn(
          'No selection timestamp found for alert:',
          alertId,
          'Current stored timestamps:',
          storedTimes
        );
      }
    } catch (error) {
      console.error('Error tracking alert resolution:', error);
    }
  };

  const trackWellnessCheckCount = (checkCount: number, shiftId:string) => {
    try {
      posthog.capture('wellness_check_count_per_shift', {
        user_id: auth.user?.id,
        check_count: checkCount,
        shift_id: shiftId,
        timestamp: new Date().toISOString(),
        distinct_id: auth.user?.id,
      });
    } catch (error) {
      console.error('Error tracking wellness check count:', error);
    }
  };

  const trackSLABreachCount = (breachCount: number, shiftId:string) => {
    try {
      posthog.capture('SLA_breach_count_per_shift', {
        user_id: auth.user?.id,
        breach_count: breachCount,
        shift_id: shiftId,
        timestamp: new Date().toISOString(),
        distinct_id: auth.user?.id,
      });
    } catch (error) {
      console.error('Error tracking SLA breach count:', error);
    }
  };

  const trackUnreadEventsClosed = (alertId: string, events: MonitorAlertEventDto[]) => {
    try {
      posthog.capture('unread_events_closed', {
        user_id: auth.user?.id,
        alert_id: alertId,
        timestamp: new Date().toISOString(),
        distinct_id: auth.user?.id,
        number_of_unread_events: events?.length || 0,
      });
    } catch (error) {
      console.error('Error tracking unread events closed:', error);
    }
  };

  const trackShiftBreak = () => {
    try {
      const timestamp = new Date().toISOString();
      // Store break start time
      localStorage.setItem(SHIFT_BREAK_START_KEY, timestamp);

      posthog.capture('monitor_shift_break', {
        username: auth.user?.username,
        user_id: auth.user?.id,
        timestamp: timestamp,
        distinct_id: auth.user?.id,
      });
    } catch (error) {
      console.error('Error tracking shift break:', error);
    }
  };

  const trackShiftResume = () => {
    try {
      const timestamp = new Date().toISOString();
      const breakStartTime = localStorage.getItem(SHIFT_BREAK_START_KEY);

      // Calculate break duration in seconds
      let breakDurationInSeconds = 0;
      let breakDurationReadable = '0';

      if (breakStartTime) {
        const startTime = new Date(breakStartTime).getTime();
        const endTime = new Date(timestamp).getTime();
        breakDurationInSeconds = Math.round((endTime - startTime) / 1000);

        // Convert to human readable format
        const hours = Math.floor(breakDurationInSeconds / 3600);
        const minutes = Math.floor((breakDurationInSeconds % 3600) / 60);
        const seconds = breakDurationInSeconds % 60;

        const parts = [];
        if (hours > 0) {
          parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
        }
        if (minutes > 0) {
          parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
        }
        if (seconds > 0 || parts.length === 0) {
          parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
        }

        breakDurationReadable = parts.join(' ');

        // Clear the stored break start time
        localStorage.removeItem(SHIFT_BREAK_START_KEY);
      }

      posthog.capture('monitor_shift_resume', {
        user_id: auth.user?.id,
        username: auth.user?.username,
        timestamp: timestamp,
        distinct_id: auth.user?.id,
        break_duration_seconds: `${breakDurationInSeconds}s`,
        break_duration: breakDurationReadable,
      });
    } catch (error) {
      console.error('Error tracking shift resume:', error);
    }
  };

  return {
    trackAlertSelection,
    trackAlertResolved,
    trackWellnessCheckCount,
    trackSLABreachCount,
    trackUnreadEventsClosed,
    trackShiftStart: () => trackShiftStart(auth.user),
    trackShiftEnd: () => trackShiftEnd(auth.user),
    trackShiftBreak,
    trackShiftResume,
  };
};
